import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const DynamicQuestionnaire = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [completedData, setCompletedData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});

  // Default to comprehensive if no type provided
  const questionnaireType = type || 'comprehensive';

  useEffect(() => {
    loadQuestionnaire();
  }, [questionnaireType]);

  const loadQuestionnaire = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/dynamic-questionnaire/${questionnaireType}`);
      
      if (response.data.completed) {
        setIsCompleted(true);
        setCompletedData(response.data);
      } else {
        setQuestionnaire(response.data.questionnaire);
        if (response.data.savedResponses) {
          setAnswers(response.data.savedResponses);
        }
      }
    } catch (error) {
      console.error('Failed to load questionnaire:', error);
      toast.error('Failed to load questionnaire');
    } finally {
      setLoading(false);
    }
  };

  const validateField = (field, value) => {
    const errors = {};
    
    if (field.required && (!value || value === '')) {
      errors[field.id] = `${field.label} is required`;
    }
    
    if (field.type === 'number' && value !== '') {
      const num = parseFloat(value);
      if (isNaN(num)) {
        errors[field.id] = 'Please enter a valid number';
      } else {
        if (field.min !== undefined && num < field.min) {
          errors[field.id] = `Minimum value is ${field.min}`;
        }
        if (field.max !== undefined && num > field.max) {
          errors[field.id] = `Maximum value is ${field.max}`;
        }
      }
    }
    
    return errors;
  };

  const validateAllFields = (fields, answersData) => {
    const errors = {};
    
    const validateFieldRecursive = (fieldList, prefix = '') => {
      fieldList.forEach(field => {
        const fieldId = prefix ? `${prefix}.${field.id}` : field.id;
        const value = answersData[fieldId];
        
        if (field.type === 'group' && field.fields) {
          validateFieldRecursive(field.fields, fieldId);
        } else {
          const fieldErrors = validateField(field, value);
          Object.assign(errors, fieldErrors);
        }
      });
    };
    
    validateFieldRecursive(fields);
    return errors;
  };

  const handleAnswerChange = (fieldId, value) => {
    setAnswers(prev => ({ ...prev, [fieldId]: value }));
    
    // Clear validation error for this field
    if (validationErrors[fieldId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const handleNestedAnswerChange = (parentId, fieldId, value) => {
    const nestedFieldId = `${parentId}.${fieldId}`;
    handleAnswerChange(nestedFieldId, value);
  };

  const saveProgress = async () => {
    try {
      setSaving(true);
      await api.post(`/dynamic-questionnaire/${questionnaireType}/save`, { responses: answers });
      toast.success('Progress saved successfully');
    } catch (error) {
      console.error('Failed to save progress:', error);
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!questionnaire || !questionnaire.fields) {
      toast.error('Questionnaire data not available');
      return;
    }
    
    // Validate all fields
    const errors = validateAllFields(questionnaire.fields, answers);
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await api.post(`/dynamic-questionnaire/${questionnaireType}/submit`, { responses: answers });
      
      toast.success('Questionnaire submitted successfully!');
      
      // Update state with completion data
      setIsCompleted(true);
      setCompletedData(response.data);
      
      // Redirect after a delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
      
    } catch (error) {
      console.error('Failed to submit questionnaire:', error);
      toast.error(error.response?.data?.error || 'Failed to submit questionnaire');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field, parentPath = '') => {
    const fieldId = parentPath ? `${parentPath}.${field.id}` : field.id;
    const value = answers[fieldId] || '';
    const error = validationErrors[fieldId];

    switch (field.type) {
      case 'number':
        return (
          <div key={fieldId} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="number"
              min={field.min}
              max={field.max}
              step={field.step || '1'}
              value={value}
              onChange={(e) => handleAnswerChange(fieldId, e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={field.placeholder || ''}
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            {field.help && <p className="text-gray-500 text-sm mt-1">{field.help}</p>}
          </div>
        );

      case 'radio_simple':
        return (
          <div key={fieldId} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {field.options.map((option) => (
                <label key={option} className="flex items-center">
                  <input
                    type="radio"
                    name={fieldId}
                    value={option}
                    checked={value === option}
                    onChange={(e) => handleAnswerChange(fieldId, e.target.value)}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">{option}</span>
                </label>
              ))}
            </div>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            {field.help && <p className="text-gray-500 text-sm mt-1">{field.help}</p>}
          </div>
        );

      case 'select':
        return (
          <div key={fieldId} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              value={value}
              onChange={(e) => handleAnswerChange(fieldId, e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select an option</option>
              {field.options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            {field.help && <p className="text-gray-500 text-sm mt-1">{field.help}</p>}
          </div>
        );

      case 'scale_0_10':
        return (
          <div key={fieldId} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">0</span>
              <input
                type="range"
                min="0"
                max="10"
                value={value || 5}
                onChange={(e) => handleAnswerChange(fieldId, e.target.value)}
                className="flex-1"
              />
              <span className="text-sm text-gray-500">10</span>
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                {value || 5}
              </span>
            </div>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            {field.help && <p className="text-gray-500 text-sm mt-1">{field.help}</p>}
          </div>
        );

      case 'group':
        return (
          <div key={fieldId} className="mb-8 p-4 border border-gray-200 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{field.label}</h3>
            <div className="space-y-4">
              {field.fields.map((subField) => renderField(subField, fieldId))}
            </div>
          </div>
        );

      default:
        return (
          <div key={fieldId} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => handleAnswerChange(fieldId, e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={field.placeholder || ''}
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            {field.help && <p className="text-gray-500 text-sm mt-1">{field.help}</p>}
          </div>
        );
    }
  };

  const renderProgressBar = () => {
    if (!questionnaire || !questionnaire.fields) return null;
    
    const totalFields = countTotalFields(questionnaire.fields);
    const answeredFields = countAnsweredFields(questionnaire.fields, answers);
    const progress = (answeredFields / totalFields) * 100;
    
    return (
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Progress</span>
          <span>{answeredFields} / {totalFields}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  };

  const countTotalFields = (fields) => {
    let count = 0;
    fields.forEach(field => {
      if (field.type === 'group' && field.fields) {
        count += countTotalFields(field.fields);
      } else {
        count++;
      }
    });
    return count;
  };

  const countAnsweredFields = (fields, answersData) => {
    let count = 0;
    fields.forEach(field => {
      if (field.type === 'group' && field.fields) {
        count += countAnsweredFields(field.fields, answersData);
      } else {
        const fieldId = field.id;
        if (answersData[fieldId] && answersData[fieldId] !== '') {
          count++;
        }
      }
    });
    return count;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (isCompleted && completedData) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <svg className="w-8 h-8 text-green-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <h2 className="text-2xl font-bold text-green-800">Questionnaire Completed ✅</h2>
          </div>
          
          <p className="text-green-700 mb-4">
            You completed this questionnaire on {new Date(completedData.completed_at).toLocaleDateString()}.
          </p>
          
          {completedData.health_score && (
            <div className="mb-4">
              <h3 className="font-semibold text-green-800 mb-2">Your Health Score</h3>
              <div className="text-3xl font-bold text-green-600">
                {typeof completedData.health_score === 'number' ? completedData.health_score.toFixed(1) : completedData.health_score}
              </div>
            </div>
          )}
          
          {completedData.ml_predictions && (
            <div className="mb-4">
              <h3 className="font-semibold text-green-800 mb-2">Health Predictions</h3>
              <div className="space-y-2">
                {Object.entries(completedData.ml_predictions).map(([organ, data]) => (
                  <div key={organ} className="bg-white p-2 rounded border">
                    <span className="font-medium capitalize">{organ}:</span>
                    <span className="ml-2 text-gray-600">
                      {data.score ? `Score: ${data.score}` : 
                       data.health_score ? `Health Score: ${data.health_score}` :
                       data.predicted_class ? `Risk: ${data.predicted_class}` : 'Processed'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-red-800 mb-2">Questionnaire Not Found</h2>
          <p className="text-red-700">The requested questionnaire could not be loaded.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{questionnaire.title}</h1>
          <p className="text-gray-600">{questionnaire.description}</p>
        </div>

        {renderProgressBar()}

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {questionnaire.fields.map((field) => renderField(field))}
          </div>

          <div className="flex justify-between items-center mt-8">
            <button
              type="button"
              onClick={saveProgress}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Progress'}
            </button>

            <div className="space-x-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit Questionnaire'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DynamicQuestionnaire;
